import clsx from "clsx";
import { ChevronDown, Search, X } from "lucide-react";
import { forwardRef, useCallback, useEffect, useId, useRef, useState, type ChangeEvent, type KeyboardEvent } from "react";
import type { UseInfiniteQueryResult } from "@tanstack/react-query";
import type { WholesaleOrderSummary, WholesaleSearchHit } from "../../../shared/contracts.js";
import {
  flattenPages,
  type InfinitePages,
} from "../../api/caseQueries.js";
import { useCustomer, useWholesaleList, useWholesaleSearch } from "../../api/wholesaleQueries.js";
import { AsyncPanel } from "../../ui/AsyncPanel.js";
import { Button } from "../../ui/Button.js";
import { StatusBadge } from "../../ui/StatusBadge.js";
import { SEARCH_DEBOUNCE_MS } from "../cases/workspace/CaseListPane.js";
import { formatShortInstant, progressLabel, progressPercent, resolveStatusTone } from "../cases/presentation.js";
import { serverViewOf, WHOLESALE_VIEW_PRESENTATION } from "./presentation.js";
import type { WholesaleUrlStateApi } from "./useWholesaleUrlState.js";

/**
 * The centre list of the wholesale workspace (spec §4.2): the list's name
 * and description, the search field (which *is* the Search list's query),
 * the customer filter when the list is narrowed to one customer's history,
 * and the rows with "Show more" for the next page.
 *
 * Open, Arrived · 36h, History and All orders read `GET /api/wholesale/orders`
 * for their view; Search reads `GET /api/wholesale/orders/search` for the
 * typed query. Search and customer are URL state; the field keeps a local
 * draft and writes it after a short pause, like the service list. Every
 * non-happy state is `AsyncPanel`'s.
 */

export interface WholesaleListProps {
  url: WholesaleUrlStateApi;
  className?: string;
}

export function WholesaleList({ url, className }: WholesaleListProps) {
  const { state } = url;
  const headingId = useId();
  const searchId = useId();
  const presentation = WHOLESALE_VIEW_PRESENTATION[state.view];
  const serverView = serverViewOf(state.view);
  const searching = serverView === null;

  const list = useWholesaleList(
    { view: serverView ?? "open", customerId: state.customerId },
    { enabled: !searching },
  );
  const search = useWholesaleSearch(searching ? state.query : "");
  const customer = useCustomer(state.customerId);

  return (
    <section className={clsx("workspace__list", "list-pane", className)} aria-labelledby={headingId}>
      <header className="list-pane__header">
        <div className="list-pane__title">
          <h1 id={headingId} className="list-pane__heading">
            {presentation.label}
          </h1>
          <p className="list-pane__description">{presentation.description}</p>
        </div>

        <div className="list-pane__controls">
          {searching ? (
            <SearchField id={searchId} value={state.query} onCommit={url.setQuery} />
          ) : null}
          {state.customerId ? (
            <div className="list-pane__filters" role="group" aria-label="Filters">
              <span className="filter-chip">
                <span className="filter-chip__label">
                  Customer: {customer.data?.name ?? "…"}
                </span>
                <Button
                  variant="quiet"
                  iconOnly
                  icon={<X />}
                  className="filter-chip__clear"
                  onClick={() => url.setCustomer(null)}
                >
                  Clear customer filter
                </Button>
              </span>
            </div>
          ) : null}
        </div>
      </header>

      {searching ? (
        <SearchRows
          search={search}
          query={state.query}
          selectedOrderId={state.orderId}
          onSelect={url.selectOrder}
          emptyTitle={presentation.emptyTitle}
          emptyDescription={presentation.emptyDescription}
        />
      ) : (
        <OrderRows
          list={list}
          selectedOrderId={state.orderId}
          onSelect={url.selectOrder}
          hasFilters={state.customerId !== null}
          emptyTitle={presentation.emptyTitle}
          emptyDescription={presentation.emptyDescription}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

function SearchField({ id, value, onCommit }: { id: string; value: string; onCommit: (query: string) => void }) {
  const [draft, setDraft] = useState(value);
  const committed = useRef(value);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (value !== committed.current) {
      committed.current = value;
      setDraft(value);
    }
  }, [value]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const commit = useCallback(
    (next: string) => {
      const trimmed = next.trim();
      if (trimmed === committed.current) return;
      committed.current = trimmed;
      onCommit(trimmed);
    },
    [onCommit],
  );

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setDraft(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => commit(next), SEARCH_DEBOUNCE_MS);
  };

  return (
    <div className="list-pane__search" role="search">
      <Search className="list-pane__search-icon" aria-hidden="true" focusable="false" />
      <input
        id={id}
        type="search"
        className="text-input list-pane__search-input"
        data-size="desktop"
        aria-label="Search wholesale orders"
        placeholder="WS number, customer, reference, R&R, part…"
        autoComplete="off"
        spellCheck={false}
        autoFocus
        value={draft}
        onChange={onChange}
        onBlur={() => {
          if (timer.current) clearTimeout(timer.current);
          commit(draft);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            if (timer.current) clearTimeout(timer.current);
            commit(draft);
          }
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

function OrderRows({
  list,
  selectedOrderId,
  onSelect,
  hasFilters,
  emptyTitle,
  emptyDescription,
}: {
  list: UseInfiniteQueryResult<InfinitePages<WholesaleOrderSummary>>;
  selectedOrderId: string | null;
  onSelect: (orderId: string) => void;
  hasFilters: boolean;
  emptyTitle: string;
  emptyDescription: string;
}) {
  const rows = flattenPages(list.data);
  const loaded = list.data !== undefined;
  return (
    <RowsPanel
      rows={rows}
      loaded={loaded}
      isPending={list.isPending}
      error={list.error}
      isPlaceholderData={list.isPlaceholderData}
      hasNextPage={list.hasNextPage}
      isFetchingNextPage={list.isFetchingNextPage}
      isFetchNextPageError={list.isFetchNextPageError}
      fetchNextPage={() => {
        void list.fetchNextPage();
      }}
      refetch={() => {
        void list.refetch();
      }}
      selectedOrderId={selectedOrderId}
      onSelect={onSelect}
      emptyTitle={hasFilters ? "No orders for this customer" : emptyTitle}
      emptyDescription={hasFilters ? "This customer has no orders on this list. Clear the customer filter to see every order." : emptyDescription}
    />
  );
}

function SearchRows({
  search,
  query,
  selectedOrderId,
  onSelect,
  emptyTitle,
  emptyDescription,
}: {
  search: UseInfiniteQueryResult<InfinitePages<WholesaleSearchHit>>;
  query: string;
  selectedOrderId: string | null;
  onSelect: (orderId: string) => void;
  emptyTitle: string;
  emptyDescription: string;
}) {
  const hits = flattenPages(search.data);
  const rows = hits.map((hit) => hit.order);
  if (query.trim() === "") {
    return (
      <div className="list-pane__body">
        <AsyncPanel isEmpty emptyTitle={emptyTitle} emptyDescription={emptyDescription}>
          {null}
        </AsyncPanel>
      </div>
    );
  }
  const loaded = search.data !== undefined;
  return (
    <RowsPanel
      rows={rows}
      loaded={loaded}
      isPending={search.isPending}
      error={search.error}
      isPlaceholderData={search.isPlaceholderData}
      hasNextPage={search.hasNextPage}
      isFetchingNextPage={search.isFetchingNextPage}
      isFetchNextPageError={search.isFetchNextPageError}
      fetchNextPage={() => {
        void search.fetchNextPage();
      }}
      refetch={() => {
        void search.refetch();
      }}
      selectedOrderId={selectedOrderId}
      onSelect={onSelect}
      emptyTitle="No orders match"
      emptyDescription="Try a WS number, customer, reference, R&R number, part number or description."
    />
  );
}

function RowsPanel({
  rows,
  loaded,
  isPending,
  error,
  isPlaceholderData,
  hasNextPage,
  isFetchingNextPage,
  isFetchNextPageError,
  fetchNextPage,
  refetch,
  selectedOrderId,
  onSelect,
  emptyTitle,
  emptyDescription,
}: {
  rows: readonly WholesaleOrderSummary[];
  loaded: boolean;
  isPending: boolean;
  error: unknown;
  isPlaceholderData: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isFetchNextPageError: boolean;
  fetchNextPage: () => void;
  refetch: () => void;
  selectedOrderId: string | null;
  onSelect: (orderId: string) => void;
  emptyTitle: string;
  emptyDescription: string;
}) {
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const moveFocus = (index: number, direction: -1 | 1) => {
    rowRefs.current[index + direction]?.focus();
  };
  return (
    <div className="list-pane__body" data-updating={isPlaceholderData ? "true" : undefined}>
      <AsyncPanel
        isLoading={isPending}
        error={loaded ? undefined : error}
        refreshError={loaded ? error : undefined}
        refreshTitle={isFetchNextPageError ? "Couldn't load more orders." : "Couldn't refresh the list."}
        isEmpty={rows.length === 0}
        onRetry={refetch}
        loadingLabel="Loading orders…"
        emptyTitle={emptyTitle}
        emptyDescription={emptyDescription}
      >
        <ul className="case-list" aria-label="Wholesale orders">
          {rows.map((item, index) => (
            <WholesaleOrderRow
              key={item.id}
              ref={(element) => {
                rowRefs.current[index] = element;
              }}
              item={item}
              selected={item.id === selectedOrderId}
              onSelect={onSelect}
              onMoveFocus={(direction) => moveFocus(index, direction)}
            />
          ))}
        </ul>
        {hasNextPage ? (
          <div className="list-pane__more">
            <Button variant="secondary" icon={<ChevronDown />} busy={isFetchingNextPage} onClick={fetchNextPage}>
              Show more
            </Button>
          </div>
        ) : null}
      </AsyncPanel>
    </div>
  );
}

export interface WholesaleOrderRowProps {
  item: WholesaleOrderSummary;
  selected: boolean;
  onSelect: (orderId: string) => void;
  onMoveFocus?: (direction: -1 | 1) => void;
}

/**
 * One row: the customer, the WS number with the reference and R&R number,
 * when the order last changed, the status badge, the progress and the
 * unread marker — every value as the server returned it. A button, as on
 * the service side: selecting it opens the order in the inspector.
 */
export const WholesaleOrderRow = forwardRef<HTMLButtonElement, WholesaleOrderRowProps>(function WholesaleOrderRow(
  { item, selected, onSelect, onMoveFocus },
  ref,
) {
  const tone = resolveStatusTone(item.status, item.needsAttention);
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!onMoveFocus) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      onMoveFocus(1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      onMoveFocus(-1);
    }
  };
  return (
    <li className="case-list__item">
      <button
        ref={ref}
        type="button"
        className={clsx("case-row", "case-row--wholesale", selected && "case-row--selected")}
        aria-current={selected ? "true" : undefined}
        data-order-id={item.id}
        data-status={item.status}
        data-tone={tone}
        data-unread={item.unread ? "true" : undefined}
        onClick={() => onSelect(item.id)}
        onKeyDown={onKeyDown}
      >
        <span className="case-row__marker" aria-hidden="true" />
        <span className="case-row__body">
          <span className="case-row__headline">
            <span className="case-row__vehicle">{item.customerName}</span>
            {item.unread ? (
              <span className="case-row__unread">
                <span className="visually-hidden">Unread</span>
              </span>
            ) : null}
          </span>
          <span className="case-row__identity">
            <span className="case-row__number">{item.number}</span>
            {item.customerReference ? (
              <>
                <span className="case-row__dot" aria-hidden="true">
                  ·
                </span>
                <span className="case-row__ro">Ref {item.customerReference}</span>
              </>
            ) : null}
            {item.rrNumber ? (
              <>
                <span className="case-row__dot" aria-hidden="true">
                  ·
                </span>
                <span className="case-row__vin">R&amp;R {item.rrNumber}</span>
              </>
            ) : null}
          </span>
          <span className="case-row__meta">
            <span className="case-row__updated">
              <span className="case-row__meta-label">Updated</span>{" "}
              <time dateTime={item.updatedAt}>{formatShortInstant(item.updatedAt)}</time>
            </span>
          </span>
        </span>
        <span className="case-row__aside">
          <StatusBadge status={item.status} needsAttention={item.needsAttention} />
          <span className="case-row__progress">
            <span className="case-row__progress-label">{progressLabel(item.quantityVerified, item.quantityOrdered)}</span>
            <span className="case-row__bar" aria-hidden="true">
              <span
                className="case-row__bar-fill"
                style={{ width: `${progressPercent(item.quantityVerified, item.quantityOrdered)}%` }}
              />
            </span>
          </span>
        </span>
      </button>
    </li>
  );
});
